import { ArrowUp } from 'lucide-react'
import { DocBlockView } from './DocBlocks'
import { sectionNumber } from './DocsToc'
import type { DocBlock, DocSection } from './types'

/** Prose blocks keep a readable measure; tables and live panels may use the full width. */
function isWide(block: DocBlock): boolean {
  return block.type === 'table' || block.type === 'kv' || block.type === 'live'
}

interface Props {
  section: DocSection
  index: number
  onTop: () => void
}

export function DocSectionView({ section, index, onTop }: Props) {
  const Icon = section.icon
  const headingId = `${section.id}-title`
  return (
    <section
      id={section.id}
      aria-labelledby={headingId}
      className="docs-section scroll-mt-24 rounded-lg border border-line bg-surface px-5 py-6 shadow-[0_1px_2px_rgb(22_62_58/0.04)] sm:px-8 sm:py-8 lg:scroll-mt-6"
    >
      <header className="mb-6 flex items-start gap-4">
        <span
          aria-hidden="true"
          className="hidden size-11 shrink-0 place-items-center rounded-md border border-teal-100 bg-teal-50 text-teal sm:grid [&>svg]:size-5"
        >
          <Icon />
        </span>
        <div className="min-w-0">
          <p className="font-mono text-xs font-semibold tracking-[0.08em] text-teal-500">
            {sectionNumber(index)}
          </p>
          <h2
            id={headingId}
            tabIndex={-1}
            className="mt-0.5 font-display text-[22px] font-semibold leading-tight tracking-[var(--tracking-heading)] text-ink focus:outline-none sm:text-2xl"
          >
            {section.title}
          </h2>
          <p className="mt-1.5 max-w-[75ch] text-[15px] leading-relaxed text-sage">
            {section.summary}
          </p>
        </div>
      </header>
      <div className="flex flex-col gap-4 text-[15px] leading-[1.7] text-ink-soft">
        {section.blocks.map((block, blockIndex) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: blocks are static, ordered content
            key={blockIndex}
            className={isWide(block) ? 'min-w-0' : 'min-w-0 max-w-[75ch]'}
          >
            <DocBlockView block={block} />
          </div>
        ))}
      </div>
      <p className="mt-6 border-t border-line-soft pt-4 print:hidden">
        <button
          type="button"
          onClick={onTop}
          className="inline-flex items-center gap-1.5 rounded-sm text-xs font-semibold text-teal hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal"
        >
          <ArrowUp aria-hidden="true" className="size-3.5" />
          Back to contents
        </button>
      </p>
    </section>
  )
}
